import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavVariant } from '../App';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, Database, Dot, Text } from 'lucide-react';
import { formatLastModified } from './NestedFeed';
import '../styles/BDDash.css';
import Card from './Card';

const formatContentWithMentions = (content, users) => {
    if (!content || !users || users.length === 0) return content;

    const mentionPattern = /@([a-zA-Z0-9_]+)/g;
    
    return content.replace(mentionPattern, (match, username) => {
        const mentionedUser = users.find(u => u.user === username);
        if (mentionedUser) {
            return `<span class="tag-mention">@${username}</span>`;
        }
        return match;
    });
};

const clearHtmlTags = (html) => {
    if (!html || typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

const normalizeFeedContent = (html) => {
    if (!html || typeof html !== 'string') return '';
    const withNewlines = html.replace(/<br\s*\/?>/gi, '\n');
    const textOnly = clearHtmlTags(withNewlines);
    return textOnly.replace(/\r\n/g, '\n');
};

const renderEmphasis = (text) => {
    if (!text || typeof text !== 'string') return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            const content = part.slice(2, -2);
            return (
                <span key={`em-${index}`} style={{ color: 'var(--fill)' }}>
                    {content}
                </span>
            );
        }
        return <React.Fragment key={`em-${index}`}>{part}</React.Fragment>;
    });
};

const BDDashboard = ({ user, hideHeader = false, pageContainerClassName = null, selectedBdUserOverride = null, onSelectedBdUserOverride = null, onUsersLoaded = null, defaultViewType = 'report' }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [bdUsers, setBdUsers] = useState([]);
    const [selectedBdUser, setSelectedBdUser] = useState(null);
    const [hasUserSelected, setHasUserSelected] = useState(false);
    const [viewType, setViewType] = useState(defaultViewType);
    const [includeArchived, setIncludeArchived] = useState(false);
    const [activeCard, setActiveCard] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'create_date', direction: 'desc', type: 'date' });
    const tableScrollRef = useRef(null);
    // Prevents the override-change useEffect from re-fetching when we're the ones who just synced the value
    const skipNextOverrideRef = useRef(null);
    const navVariant = useNavVariant();
    const effectiveSelectedBdUser = selectedBdUserOverride !== null && selectedBdUserOverride !== undefined
        ? selectedBdUserOverride
        : selectedBdUser;
    const updateSelectedBdUser = (value) => {
        if (onSelectedBdUserOverride) {
            onSelectedBdUserOverride(value);
        } else {
            setSelectedBdUser(value);
        }
        setHasUserSelected(true);
    };

    const buildDashUrl = (userId, withArchived) => {
        const params = new URLSearchParams();
        params.set('include_summary', '1');
        if (withArchived) {
            params.set('include_archived', 'true');
        }
        if (userId) {
            params.set('user', userId);
        }
        return `https://api.casedb.co/bddash.php?${params.toString()}`;
    };

    const fetchDashData = async (userId, withArchived) => {
        const resp = await fetch(buildDashUrl(userId, withArchived), { credentials: 'include' });
        return resp.json();
    };

    const loadDashData = async (userId, withArchived) => {
        setLoading(true);
        try {
            const json = await fetchDashData(userId, withArchived);
            setData(json);
        } catch (e) {
            setData({ success: false, error: String(e) });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const json = await fetchDashData(null, includeArchived);
                console.debug('bddash initial response', json);
                setData(json);
                if (json && Array.isArray(json.users) && json.users.length > 0) {
                    setBdUsers(json.users);
                    if (onUsersLoaded) {
                        onUsersLoaded(json.users);
                    }
                }
                const autoUserId = json?.selected_user_id
                    ? String(json.selected_user_id)
                    : (json?.users?.length > 0 ? String(json.users[0].id) : null);

                setSelectedBdUser(prev => (prev !== null ? prev : autoUserId));

                // Sync back to parent dropdown (e.g. Homepage) without triggering a re-fetch
                if (autoUserId && onSelectedBdUserOverride && !selectedBdUserOverride) {
                    skipNextOverrideRef.current = autoUserId;
                    onSelectedBdUserOverride(autoUserId);
                }
            } catch (e) {
                setData({ success: false, error: String(e) });
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Do not auto-select current user; backend will handle initial filtering and session checks.

    // When selected BD user changes, re-fetch the BD cases for that user
    useEffect(() => {
        const hasOverrideSelection = selectedBdUserOverride !== null && selectedBdUserOverride !== undefined;

        if (hasOverrideSelection) {
            // Skip if this override change was triggered by our own auto-selection sync
            if (skipNextOverrideRef.current !== null && String(skipNextOverrideRef.current) === String(selectedBdUserOverride)) {
                skipNextOverrideRef.current = null;
                return;
            }
            loadDashData(selectedBdUserOverride, includeArchived);
            return;
        }

        if (!includeArchived && !hasUserSelected) return;
        if (hasUserSelected && !effectiveSelectedBdUser) return;
        loadDashData(hasUserSelected ? effectiveSelectedBdUser : null, includeArchived);
    }, [effectiveSelectedBdUser, includeArchived, hasUserSelected, selectedBdUserOverride]);


    const reportColumns = useMemo(() => ([
        { key: 'contact_full_name', label: 'Contact Full Name', type: 'text' },
        { key: 'case_type', label: 'Case Type', type: 'text' },
        { key: 'incident_date', label: 'Incident Date', type: 'date' },
        { key: 'first_primary', label: 'First Primary', type: 'text' },
        { key: 'case_phase', label: 'Case Phase', type: 'text' },
        { key: 'date_contract_signed', label: 'Date Contract Signed', type: 'date' },
        { key: 'settlement_date', label: 'Settlement Date', type: 'date' },
        { key: 'final_settlement', label: 'Final Settlement', type: 'currency' },
        { key: 'projected_settlement_value', label: 'Projected Settlement Value', type: 'currency' },
        { key: 'projected_net_attorney_fee', label: 'Projected Net Attorney Fee', type: 'currency' },
        { key: 'db_origination', label: 'DB Origination', type: 'text' },
        { key: 'marketing_source', label: 'Marketing Source', type: 'text' },
        { key: 'marketing_lead', label: 'Marketing Lead', type: 'text' },
        { key: 'referral_contact', label: 'Referral Contact', type: 'text' },
        { key: 'notes', label: 'Notes', type: 'text' },
        { key: 'last_modified', label: 'Last Modified', type: 'last_modified' }
    ]), []);

    const getSortValue = (row, type, key) => {
        const value = row?.[key];
        if (value === null || value === undefined || value === '') return null;

        if (type === 'date' || type === 'last_modified') {
            const date = value instanceof Date ? value : new Date(value);
            const time = date.getTime();
            return Number.isNaN(time) ? null : time;
        }

        if (type === 'currency') {
            const numericValue = typeof value === 'number'
                ? value
                : Number(String(value).replace(/[^0-9.-]/g, ''));
            return Number.isFinite(numericValue) ? numericValue : null;
        }

        return String(value).toLowerCase();
    };

    const sortedCases = useMemo(() => {
        const list = data?.cases?.slice() || [];
        const { key, direction, type } = sortConfig || {};
        if (!key) {
            return list.sort((a, b) => new Date(b.create_date) - new Date(a.create_date));
        }

        const sortDir = direction === 'asc' ? 1 : -1;
        return list.sort((a, b) => {
            const aVal = getSortValue(a, type, key);
            const bVal = getSortValue(b, type, key);

            if (aVal === null && bVal === null) return 0;
            if (aVal === null) return 1;
            if (bVal === null) return -1;

            if (aVal < bVal) return -1 * sortDir;
            if (aVal > bVal) return 1 * sortDir;
            return 0;
        });
    }, [data?.cases, sortConfig]);

    const columnWidths = useMemo(() => {
        const baseCharWidth = 9;
        const padding = 40;
        const minWidth = 140;
        const maxWidth = 420;

        return Object.fromEntries(reportColumns.map(col => {
            const maxContentLength = Math.max(
                col.label.length,
                ...sortedCases.map(item => String(item?.[col.key] ?? '').length)
            );
            let width = maxContentLength * baseCharWidth + padding;
            width = Math.max(minWidth, Math.min(maxWidth, width));
            return [col.key, `${width}px`];
        }));
    }, [reportColumns, sortedCases]);

    const formatReportDate = (value) => {
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return String(value ?? '');
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        }).format(date);
    };

    const formatReportDateTime = (value) => {
        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) return String(value ?? '');
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).format(date);
    };

    const formatReportValue = (value, type) => {
        if (value === null || value === undefined || value === '') return '';

        if (type === 'last_modified') {
            return formatReportDateTime(value);
        }

        if (type === 'date') {
            return formatReportDate(value);
        }

        if (type === 'currency') {
            const numericValue = typeof value === 'number'
                ? value
                : Number(String(value).replace(/[^0-9.-]/g, ''));
            if (!Number.isFinite(numericValue)) return String(value);
            return `$${numericValue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;
        }

        return String(value);
    };

    if (loading) return <div style={{padding:20}}>Loading BD Dashboard...</div>;

    const access = data?.access_level || user?.access_level || null;
    const isAdminAccess = typeof access === 'string' && ['admin', 'global admin'].includes(access.toLowerCase());

    const containerClassName = pageContainerClassName
        ? `${pageContainerClassName}`
        : `page-container homepage ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`;

    return (
        <div className={containerClassName}>
            {!hideHeader && (
                <div id='page-header'>
                    <h2>BD Dashboard</h2>
                    {isAdminAccess && (
                        <div className='filter-group'>
                            <div className='select-wrapper'>
                                <select
                                    className='filter-select'
                                    value={effectiveSelectedBdUser || ''}
                                    onChange={(e) => {
                                        updateSelectedBdUser(e.target.value);
                                    }}
                                >
                                    <option value=''>-- Select User --</option>
                                    {bdUsers.map(u => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                    )}
                    <div className='this-or-that' style={{ backgroundColor: 'var(--background-color)' }}>
                        <div
                            className={`tot-option ${ viewType === 'report' ? 'active' : '' }`}
                            onClick={() => setViewType('report')}
                            title='Report'
                        >
                            <Database />
                        </div>

                        <div
                            className={`tot-option${ viewType === 'summaries' ? ' active' : '' }`}
                            onClick={() => setViewType('summaries')}
                            title='Summaries'
                        >
                            <Text />
                        </div>
                    </div>
                </div>
            )}
            <div className='bd-cases'>
                <div className='case-overview'>
                    <Card
                        data={{
                            title: "Active Cases",
                            data: (data?.active_case_count ?? data?.count ?? data?.cases?.length) || 0
                        }}
                        widthD={3}
                        isActive={activeCard === 'active'}
                        onClick={() => {
                            const next = activeCard === 'active' ? null : 'active';
                            setActiveCard(next);
                            setIncludeArchived(false);
                            if (next === null) {
                                loadDashData(hasUserSelected ? selectedBdUser : null, false);
                            }
                        }}
                    />
                    <Card data={{ title: "Final Settlement", data: formatReportValue(data?.final_settlement_total || 0, 'currency') }} widthD={3} />
                    <Card
                        data={{
                            title: "All Cases",
                            data: (data?.total_case_count ?? ((data?.active_case_count ?? data?.count ?? data?.cases?.length) || 0) + (data?.archived_case_count || 0))
                        }}
                        widthD={3}
                        isActive={activeCard === 'all'}
                        onClick={() => {
                            const next = activeCard === 'all' ? null : 'all';
                            setActiveCard(next);
                            setIncludeArchived(next === 'all');
                            if (next === null) {
                                loadDashData(hasUserSelected ? selectedBdUser : null, false);
                            }
                        }}
                    />
                </div>
                {viewType === 'summaries' ? (
                    <div className='bd-summaries'>
                        {data?.cases?.sort((a, b) => new Date(b.create_date) - new Date(a.create_date)).map((caseItem, index) => (
                            <a href={`/case/${caseItem.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }} key={index}>
                                <div className='bd-case-item' key={index}>
                                    <div className='bd-case-item-details'>
                                        <span title='Case Contact'>{caseItem?.contact?.full_name}</span>
                                        <Dot size={16} />
                                        <span title='Created'>{formatLastModified(caseItem?.create_date)[5]}</span>
                                    </div>
                                    <span className='bd-case-item-case-info'>{caseItem?.case_name}</span>
                                    <div className='horizontal divider' style={{ margin: '10px 5px', maxWidth: 'calc(100% - 10px)' }}></div>
                                    <div className='bd-case-item-latest-feed'>
                                        <div className='bd-case-item-latest-feed-header'>
                                            <span className='subtext'>Latest Activity</span>:&nbsp;
                                            <span>{formatLastModified(caseItem?.updated_at)[5]}</span>
                                            <Dot size={16} />
                                            <span>{caseItem?.latest_feed_update?.author_name} (@{caseItem?.latest_feed_update?.author_user})</span>
                                        </div>
                                        {caseItem?.recent_summary && (
                                            <div className='bd-case-item-summary'>
                                                <span className='subtext'>Recent Communications</span>
                                                <div className='recent-comms'>
                                                    {renderEmphasis(caseItem.recent_summary)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                ) : (
                    <div
                        className='bd-report-table-container'
                        ref={tableScrollRef}
                    >
                        <div className='bd-report-table-title'>
                            <span>Cases ({data?.cases?.length || 0})</span>
                        </div>
                        <table className='bd-report-table' style={{ tableLayout: 'fixed' }}>
                            <thead>
                                <tr>
                                    {reportColumns.map(col => {
                                        const isActive = sortConfig?.key === col.key;
                                        const SortIcon = isActive
                                            ? (sortConfig.direction === 'asc' ? ArrowUp : ArrowDown)
                                            : ArrowUpDown;
                                        return (
                                        <th
                                            className='small-text'
                                            key={col.key}
                                            onClick={() => {
                                                setSortConfig(prev => {
                                                    if (prev?.key === col.key) {
                                                        return {
                                                            key: col.key,
                                                            type: col.type,
                                                            direction: prev.direction === 'asc' ? 'desc' : 'asc'
                                                        };
                                                    }
                                                    return { key: col.key, type: col.type, direction: 'asc' };
                                                });
                                            }}
                                            style={{
                                                width: columnWidths[col.key],
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <div className="bd-report-cell">
                                                {col.label}
                                                <SortIcon
                                                    size={14}
                                                    style={{
                                                        marginLeft: 6,
                                                        color: isActive ? 'var(--fill)' : undefined
                                                    }}
                                                />
                                            </div>
                                        </th>
                                    );})}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedCases.map((caseItem, index) => (
                                    <tr
                                        key={`${caseItem.id}-${index}`}
                                        className="bd-report-row"
                                        role="link"
                                        tabIndex={0}
                                        onClick={() => window.open(`/case/${caseItem.id}`, '_blank', 'noopener,noreferrer')}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                window.open(`/case/${caseItem.id}`, '_blank', 'noopener,noreferrer');
                                            }
                                        }}
                                    >
                                        {reportColumns.map(col => (
                                            <td className='small-text'
                                                key={col.key}
                                                style={{
                                                    width: columnWidths[col.key]
                                                }}
                                            >
                                                <div className="bd-report-cell">
                                                    {formatReportValue(caseItem?.[col.key], col.type)}
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BDDashboard;
